import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Input } from '../../design-system-integration';
import { useAuth } from '../../context/AuthContext';

/**
 * ResetPassword Component (Fixed Version)
 *
 * This version of the ResetPassword component uses CSS variables instead of
 * styled-components' theme prop to avoid the "Cannot read properties of undefined" error.
 *
 * It follows Module M7 (Integration Points) principles by providing a clear
 * integration point between the component and the theme system.
 */

// Use CSS variables instead of theme props
const ResetPasswordForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const Title = styled.h1`
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xl);
  text-align: center;
`;

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const BackToLoginLink = styled(Link)`
  color: var(--primary-500);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  text-align: center;
  margin-top: var(--spacing-lg);
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-default);
  font-size: var(--font-size-sm);
  padding: var(--spacing-sm);
  background-color: var(--error-light);
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
`;

const SuccessMessage = styled.div`
  color: var(--success-default);
  font-size: var(--font-size-sm);
  padding: var(--spacing-sm);
  background-color: var(--success-light);
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
`;

// Create a styled button that can be used as a submit button
const SubmitButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`;

const ResetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { token } = useParams<{ token: string }>();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      setError('Please enter both, password, fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8, characters, long');
      return;
    }
    
    if (!token) {
      setError('Invalid password reset link. Please request a, new, one.');
      return;
    }
    
    try {
      setError(null);
      setSuccess(null);
      setIsLoading(true);
      
      await resetPassword(token, newPassword);
      setSuccess('Your password has been reset successfully');
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div>
      <Title>Reset Password</Title>
      <Subtitle>Create a new password for your account</Subtitle>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
      
      <ResetPasswordForm onSubmit={handleSubmit}>
        <Input
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Enter your new password"
          fullWidth
          required
        />
        
        <Input
          label="Confirm New Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your new password"
          fullWidth
          required
        />
        
        <FormActions>
          <SubmitButton type="submit">
            <Button
              variant="primary"
              fullWidth
              disabled={isLoading || !!success}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </SubmitButton>
        </FormActions>
      </ResetPasswordForm>
      
      <BackToLoginLink to="/login">Back to Login</BackToLoginLink>
    </div>
  );
};

export default ResetPassword;
